import clsx from 'clsx';
import type { ReactNode } from 'react';
import { FiChevronRight } from 'react-icons/fi';
import { Link } from 'react-router-dom';

import s from './ProfileNav.module.scss';

export type ProfileNavItem = {
  /** Identifies the section this row opens. Omit for a row that only acts. */
  id?: string;
  title: string;
  /** Used instead of `title` once a pill row runs short of room. */
  shortTitle?: string;
  /** What the section holds — the second line a tab strip had no room for. Omit it for a
      row whose title says it all; the row is then one line. */
  sub?: string;
  icon: ReactNode;
  /** Set to leave the screen instead of opening a section — renders an anchor. */
  to?: string;
  /** Runs instead of selecting, for a row that opens a dialog rather than a
      section (deleting the account, say). */
  onClick?: () => void;
  /** Recolours the row; nothing else about it changes. */
  danger?: boolean;
};

type ProfileNavProps = {
  items: readonly ProfileNavItem[];
  /** The open section's id. */
  active?: string | null;
  /** Mobile shows this list on its own, so nothing is selected yet. */
  showActive?: boolean;
  onSelect: (id: string) => void;
  ariaLabel?: string;
  /** `row` lays the rows side by side, wrapping when there is no room. */
  layout?: 'column' | 'row';
  /**
   * `pills` turns the list into a row of chips once SplitLayout folds, for a screen that
   * keeps the list above the open section (`stacked`) instead of opening sections as their
   * own screen.
   */
  mobileLayout?: 'list' | 'pills';
};

/** The inside of a row — shared because a row is a button for a section and an
    anchor for one that leaves, and the two must look identical. */
const Row = ({
  icon,
  title,
  shortTitle,
  sub,
}: {
  icon: ReactNode;
  title: string;
  shortTitle?: string;
  sub?: string;
}) => (
  <>
    <span className={s.navIcon} aria-hidden="true">
      {icon}
    </span>
    <span className={s.navText}>
      <span className={s.navTitle}>{title}</span>
      <span className={s.navTitleShort}>{shortTitle ?? title}</span>
      {sub && <span className={s.navSub}>{sub}</span>}
    </span>
    <FiChevronRight className={s.navChevron} aria-hidden="true" />
  </>
);

/**
 * A profile's section list. A row states what it holds on a second line, which a
 * tab strip had no room for; which rows there are is the caller's business, so
 * the same list serves My Profile, a guest's profile and anything after them.
 */
export const ProfileNav = ({
  items,
  active,
  showActive = true,
  onSelect,
  ariaLabel = 'Profile Sections',
  layout = 'column',
  mobileLayout = 'list',
}: ProfileNavProps) => (
  <nav
    className={clsx(
      s.sidebar,
      layout === 'row' && s.sidebarRow,
      mobileLayout === 'pills' && s.mobilePills,
    )}
    aria-label={ariaLabel}
  >
    {items.map((item) => {
      const key = item.to ?? item.id ?? item.title;
      const row = (
        <Row icon={item.icon} title={item.title} shortTitle={item.shortTitle} sub={item.sub} />
      );

      /* A row that leaves the page is a real <Link>: Back stays honest and it can
         be opened in a new tab. A div with a handler offers neither. */
      if (item.to) {
        return (
          <Link key={key} to={item.to} className={clsx(s.navItem, item.danger && s.navItemDanger)}>
            {row}
          </Link>
        );
      }

      const current = showActive && !!item.id && item.id === active;

      return (
        <button
          key={key}
          type="button"
          className={clsx(s.navItem, current && s.navItemActive, item.danger && s.navItemDanger)}
          aria-current={current ? 'page' : undefined}
          onClick={() => (item.onClick ? item.onClick() : item.id && onSelect(item.id))}
        >
          {row}
        </button>
      );
    })}
  </nav>
);
